// Curated grouping for the Experiences section.
//
// The partner catalog arrives as one flat list, and the stored partner
// subcategory is broad ("Boat Tours & Sailing" covered everything from
// catamarans to horse rides), so the section sorts every experience into a
// hand-written bucket by what the trip actually is. First match wins, so the
// order below matters: the most specific trips claim their items before the
// broader buckets can.

export const EXPERIENCES_CATEGORY = "Experiences";
export const MORE_EXPERIENCES = "More Experiences";

const BUCKETS = [
  {
    title: "Arcades & Retro Games",
    blurb: "Classic games, arcade cabinets and playful local spaces.",
    test: /arcade|retro gam|video game|gaming lounge|pinball/i,
  },
  {
    title: "Party Bus & Karaoke",
    blurb: "Group rides, sing-alongs and a moving night out.",
    test: /party bus|karaoke tour bus|karaoke bus/i,
  },
  {
    title: "City Rides & Scooter Rentals",
    blurb: "Explore San Juan by eMoped, scooter and other city rides.",
    test: /emoped|e-moped|moped|scooter rental|bike rental/i,
  },
  {
    title: "Snorkeling & Sea Life",
    blurb: "Turtles, reefs and clear Caribbean shallows.",
    test: /snorkel|turtle|reef|\bdive|diving|scuba|marine|sea trek|underwater/i,
  },
  {
    title: "Kayak & Paddleboarding",
    blurb: "Glassy lagoons, mangrove channels and after-dark paddles.",
    test: /kayak|paddle ?board|paddleboard|\bsup\b|paddle|water bike/i,
  },
  {
    title: "Horseback Riding",
    blurb: "Trail rides along the sand and through the foothills.",
    test: /horse|horses|riding|equestrian|carabal/i,
  },
  {
    title: "Fishing Charters",
    blurb: "Deep-sea and inshore trips out of San Juan and Fajardo.",
    test: /fish|fishing|angler|angling/i,
  },
  {
    title: "Wellness & Spa",
    blurb: "Slow mornings: massage, yoga and island retreats.",
    test: /\bspa\b|massage|yoga|wellness|meditation|retreat|healing/i,
  },
  {
    title: "Food, Rum & Coffee",
    blurb: "Food tours, tastings, coffee farms and distillery visits.",
    test: /food|rum|cocktail|coffee|tasting|culinary|cook|brewery|chocolate|mofongo|bar crawl|mixology|dinner|flavor|bacard|barrilito|distiller/i,
  },
  {
    title: "Culture, Music & Workshops",
    blurb: "Salsa, bomba, art and hands-on island crafts.",
    test: /salsa|dance|music|\bart\b|craft|workshop|class|photograph|photoshoot|bomba|drum|mural|cultural/i,
  },
  {
    title: "Adventure & Off-Road",
    blurb: "ATVs, ziplines, cave tubing and pure adrenaline.",
    test: /atv|utv|buggy|off-?road|4x4|zipline|zip line|\bdune|adventure park|adrenaline|go.?kart|jet ?ski|tubing|rafting|cav(e|ing)|sanctuary/i,
  },
  {
    title: "Sailing, Catamaran & Sunset Cruises",
    blurb: "Day sails, catamarans and golden-hour cruises.",
    test: /catamaran|sail|sunset|cruise|yacht|\bboat\b|ferry|schooner/i,
  },
  {
    title: "Old San Juan & City Tours",
    blurb: "The old city on foot, plus private drives around the metro.",
    test: /walking tour|\bwalk\b|historic|history|old san juan|\bfort\b|museum|architecture|city tour|ghost|sightsee|trolley|driving tour|highlights/i,
  },
  {
    title: "El Yunque & Rainforest",
    blurb: "Waterfalls, waterslides and the island's rainforest.",
    test: /yunque|rain ?forest|waterfall|waterslide|rope swing|hike|hiking|forest|jungle/i,
  },
  {
    title: "Beach & Island Day Trips",
    blurb: "Culebra, Vieques, Icacos and beach days further out.",
    test: /beach|island|culebra|vieques|icacos|fajardo|luquillo|day trip|excursion|getaway|pi[nñ]ones|cays?\b/i,
  },
];

const BLURBS = new Map(BUCKETS.map((b) => [b.title, b.blurb]));
BLURBS.set(MORE_EXPERIENCES, "Everything else travelers book on the island.");

// What the island is, before what you can do on it: culture, the party bus,
// food and the old city lead the section. The order below only decides how the
// rails are stacked — matching still runs through BUCKETS, first match wins.
export const DISPLAY_ORDER = [
  "Culture, Music & Workshops",
  "Arcades & Retro Games",
  "Party Bus & Karaoke",
  "Food, Rum & Coffee",
  "Old San Juan & City Tours",
  "Wellness & Spa",
  "Adventure & Off-Road",
  "Snorkeling & Sea Life",
  "El Yunque & Rainforest",
  "Beach & Island Day Trips",
  "Sailing, Catamaran & Sunset Cruises",
  "Kayak & Paddleboarding",
  "City Rides & Scooter Rentals",
  "Horseback Riding",
  "Fishing Charters",
  "Wellness & Spa",
  MORE_EXPERIENCES,
];

export const SORT_OPTIONS = [
  { value: "popular", label: "Most booked" },
  { value: "rating", label: "Top rated" },
  { value: "price", label: "Price: low to high" },
];

/** The bucket an experience belongs to, judged on what the trip is called. */
export const bucketFor = (item) => {
  const hay = `${item?.name || item?.title || ""} ${item?.editorial_description || ""}`;
  const hit = BUCKETS.find((bucket) => bucket.test.test(hay));
  return hit ? hit.title : MORE_EXPERIENCES;
};

const priceValue = (item) => {
  const price = Number(item?.price_from);
  return Number.isFinite(price) && price > 0 ? price : Number.POSITIVE_INFINITY;
};

const ratingValue = (item) => Number(item?.rating) || 0;

export const sortExperiences = (items, sort) => {
  if (sort === "rating") {
    return [...items].sort(
      (a, b) => ratingValue(b) - ratingValue(a) || (Number(b.review_count) || 0) - (Number(a.review_count) || 0)
    );
  }
  if (sort === "price") {
    return [...items].sort((a, b) => priceValue(a) - priceValue(b));
  }
  // "Most booked" keeps the incoming order, which is already ranked by reach.
  return items;
};

/** Every non-empty bucket, in curated order, with its items sorted. */
export const groupExperiences = (items, sort = "popular") => {
  const groups = new Map(DISPLAY_ORDER.map((title) => [title, []]));
  items.forEach((item) => groups.get(bucketFor(item)).push(item));
  return DISPLAY_ORDER.map((title) => ({
    title,
    blurb: BLURBS.get(title),
    items: sortExperiences(groups.get(title), sort),
  })).filter((group) => group.items.length > 0);
};